import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Card, CardContent, CardHeader, Collapse, Container, Grid2 as Grid, IconButton, ListSubheader, Menu, MenuItem, Stack, Toolbar, Typography, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import _ from 'lodash';
import moment from 'moment';
import { lazy, Suspense, useState } from 'react';
import AttendanceButton from '../../attendance/attendance.button';
import { attendanceApi } from '../../attendance/attendance.queries';
import { useAuth } from '../../auth/auth.provider';
import { useDialogs, useMenu } from '../../common/dialog.util';
import RatingPicker from '../../reportCard/rating.picker';
import StudentName from '../../student/student.name';
import { studentApi } from '../../student/student.queries';
import { useCalendar } from '../calendar.util';
import DailyStudentCards from './daily.student.cards';

const ActivityCompleteDialog = lazy(() => import('../../enrollment/activityComplete.dialog'));
const ActivityRescheduleDialog = lazy(() => import('../../enrollment/activityReschedule.dialog'));
const ActivitySkipDialog = lazy(() => import('../../enrollment/activitySkip.dialog'));

export default function DailyView(props) {
	const unit = 'day';
	const theme = useTheme();
	const menu = useMenu();
	const dialogs = useDialogs();
	const { can, canAny } = useAuth();

	const [forceShowAttendances, setForceShowAttendances] = useState(false);
	const [forceShowRatings, setForceShowRatings] = useState(false);

	const noop = (referenceDate, start) => {
		return moment(referenceDate);
	}

	const calendar = useCalendar(unit, noop, noop);
	const date = calendar.startDate;
	const previousDate = moment(date).subtract(1, unit);
	const previousLabel = previousDate.format('D');
	const nextDate = moment(date).add(1, unit);
	const nextLabel = nextDate.format('D');

	const useGetAll = studentApi.useGetAll();
	let students = useGetAll?.data;
	students = _.orderBy(students, 'name');
	const useGetByDate = attendanceApi.useGetByDate(date?.valueOf());
	let attendances = useGetByDate?.data;
	let ratings = _.chain(students).flatMap('ratings').filter({ field: '' + date.valueOf() }).value();

	let showAttendances = forceShowAttendances || attendances?.length < students?.length;
	let showRatings = forceShowRatings || ratings?.length < students?.length;

	let dayOfWeek = date.day();
	let isWeekend = (dayOfWeek === 6) || (dayOfWeek === 0);
	let isToday = date.isSame(new Date(), 'day');
	let isFuture = date.isAfter(new Date(), 'day');

	let isHoliday = calendar.isHoliday(date);

	const bgColours = theme.palette.background;
	let bgColour = isWeekend ? bgColours.weekend : isHoliday ? bgColours.holiday : '';
	// let bgColour = isToday ? bgColours.today : isWeekend ? bgColours.weekend : isHoliday ? bgColours.holiday : '';

	const dayTodos = _.filter(calendar.todos, { dueDate: date.valueOf() });
	const includesToday = moment().isSame(date, 'd');
	const dateSx = {
		'input': { display: 'none' },
		'fieldset': { border: 'none' },
		'.MuiInputAdornment-root': { margin: 0 },
	};

	const openActivityComplete = () => {
		menu.close();
		dialogs.open('activityComplete', getIncompleteActivities());
	};

	const openActivityReschedule = () => {
		menu.close();
		dialogs.open('activityReschedule', getIncompleteActivities());
	};

	const openActivitySkip = () => {
		menu.close();
		dialogs.open('activitySkip', getIncompleteActivities());
	};

	const getIncompleteActivities = () => {
		const incompleteActivities = _.filter(calendar.filteredActivities, activity => {
			let result = activity.status != 'Completed' && activity.status != 'Skipped';
			result = result && !activity.courseDetail.isParent;
			return result;
		});
		return incompleteActivities;
	};

	const onSubmit = () => {
		dialogs.close();
	};

	return (
		<Container sx={{ p: 2, backgroundColor: bgColour }}>
			<Toolbar disableGutters sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr' }}>
				<Box />

				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					<IconButton onClick={() => calendar.moveCalendar(-1)}>
						<ChevronLeftIcon />
					</IconButton>
					<Stack spacing={0} direction="column" alignItems="center" sx={{ minWidth: 150 }}>
						<Stack alignItems="center">
							<Typography variant="h5">{date.format('MMMM D')}</Typography>
						</Stack>
						<Typography variant="h6">{date.format('dddd')}</Typography>
					</Stack>
					<IconButton onClick={() => calendar.moveCalendar(1)}>
						<ChevronRightIcon />
					</IconButton>
				</Box>
				<Stack justifyContent="flex-end" alignItems="center" gap={0}>
					<DatePicker className="hover-subtle" sx={dateSx} value={calendar.startDate || ''} views={['year', 'month', 'day']} openTo='day' onChange={(e) => calendar.setCalendar(e)} showDaysOutsideCurrentMonth />
					{can('activity.edit') && (
						<Box>
							<IconButton className="hover-subtle" onClick={menu.open} sx={{ ml: -3 }}>
								<MoreVertIcon fontSize="inherit" />
							</IconButton>
							{menu.isOpen &&
								<Menu open={menu.isOpen} onClose={menu.close} anchorEl={menu.anchor}>
									<ListSubheader>Actions for Incomplete Activities</ListSubheader>
									<MenuItem onClick={openActivityComplete}>Mark as Completed...</MenuItem>
									<MenuItem onClick={openActivitySkip}>Mark as Skipped...</MenuItem>
									<MenuItem onClick={openActivityReschedule}>Reschedule...</MenuItem>
								</Menu>
							}
							{dialogs.isOpen?.activityComplete && (
								<Suspense fallback={null}>
									<ActivityCompleteDialog activities={dialogs.data} open={dialogs.isOpen?.activityComplete == true} onSubmit={onSubmit} onCancel={dialogs.close} />
								</Suspense>
							)}
							{dialogs.isOpen?.activityReschedule && (
								<Suspense fallback={null}>
									<ActivityRescheduleDialog activities={dialogs.data} open={dialogs.isOpen?.activityReschedule == true} onSubmit={onSubmit} onCancel={dialogs.close} />
								</Suspense>
							)}
							{dialogs.isOpen?.activitySkip && (
								<Suspense fallback={null}>
									<ActivitySkipDialog activities={dialogs.data} open={dialogs.isOpen?.activitySkip == true} onSubmit={onSubmit} onCancel={dialogs.close} />
								</Suspense>
							)}
						</Box>
					)}
				</Stack>
			</Toolbar>

			<Stack justifyContent="space-between" alignItems="center" sx={{ pb: 2 }}>
				<Box />
				<Stack gap={10} alignItems="center">

				</Stack>

			</Stack>

			<Stack direction="column" alignItems="center">
				{isHoliday && <Typography variant="h5" color="textDisabled" sx={{ pt: 3, textAlign: 'center' }}>{calendar.getHoliday(date).name}</Typography>}
			</Stack>

			{!isWeekend && !isHoliday && !isFuture && canAny(['attendance.edit', 'rating.edit']) && (
				//  && !isFuture
				<Grid container spacing={5}>
					{can('attendance.edit') &&
						<Grid size={{ xs: 12, md: 6 }}>
							<Card>
								<CardHeader title={`Attendance (${attendances?.length}/${students?.length})`}
									action={
										<IconButton onClick={() => setForceShowAttendances(!forceShowAttendances)}>
											{!showAttendances && <ExpandMoreIcon />}
											{showAttendances && <ExpandLessIcon />}
										</IconButton>
									} />
								<Collapse in={showAttendances}>
									<CardContent sx={{ py: 0 }}>
										<Typography sx={{ mt: -3 }}>Who is ready to learn today?</Typography>
										{students?.map(student => (
											<AttendanceButton key={student.id + date?.valueOf()} date={date?.valueOf()} student={student} attendance={_.find(attendances, { studentId: student.id })} />
										))}
									</CardContent>
								</Collapse>
							</Card>
						</Grid>
					}
					{can('rating.edit') &&
						<Grid size={{ xs: 12, md: 6 }}>
							<Card>
								<CardHeader title={`Evaluation (${ratings?.length}/${students?.length})`}
									action={
										<IconButton onClick={() => setForceShowRatings(!forceShowRatings)}>
											{!showRatings && <ExpandMoreIcon />}
											{showRatings && <ExpandLessIcon />}
										</IconButton>
									} />
								<Collapse in={showRatings}>
									<CardContent sx={{ py: 0 }}>
										<Typography sx={{ mt: -3 }}>How did the previous day go?</Typography>
										{students?.map(student => (
											<Stack key={student.id + date.valueOf()} alignItems="center">
												<StudentName student={student} />
												<RatingPicker rateableType="student" rateableId={student.id} field={date.valueOf()} value={_.find(student.ratings, { field: '' + date.valueOf() })?.value} />
											</Stack>
										))}
									</CardContent>
								</Collapse>
							</Card>
						</Grid>
					}
					<Grid size={{ xs: 12, md: 6 }}>

					</Grid>
				</Grid>
			)}

			<Stack direction="column" alignItems="center">
				{!calendar.filteredActivities?.length && <Typography variant="h4" color="textDisabled" sx={{ pt: 6 }}>-- No activities today --</Typography>}
			</Stack>
			<DailyStudentCards date={date} todos={dayTodos} activities={calendar.filteredActivities} groupBy={calendar.groupBy} showScore={true} isHoliday={isHoliday} />

			{calendar.fabControl}
		</Container>
	);
}